/** ToneJS based Wav player */

import { logger } from '@tunasong/models'
import type { InputNode } from 'tone'
import { PolySynth, Synth } from 'tone'
import { midiToNote } from '../events/index.js'
import type { MidiNoteEvent } from '../events/index.js'
import type { AudioInstrument } from './instrument.js'

/**
 * A polyphonic Tone synth that exists only while connected.
 *
 * Tone's PolySynth starts a garbage-collection interval on the long-lived Tone context in its constructor and
 * clears it only in `dispose()`. The engine releases an instrument with `disconnect()`, so a synth built per
 * song view stayed reachable from the context, with its voices, for the rest of the session (GH #1640). The
 * synth is therefore built on `connect()` and disposed on `disconnect()`, and a later `connect()` builds a
 * fresh one.
 */
export class BasicSynth implements AudioInstrument {
  #synth: PolySynth | null = null

  connect(node: InputNode): void {
    this.#synth ??= new PolySynth({ voice: Synth })
    this.#synth.connect(node)
  }

  disconnect(): void {
    try {
      this.#synth?.dispose()
    } catch (e) {
      logger.warn('BasicSynth dispose failed', e)
    }
    this.#synth = null
  }

  /** @param time when to play, in AudioContext-clock seconds — see AudioInstrument.onEvent (GH #1501). */
  onEvent(event: MidiNoteEvent, time?: number): void {
    logger.debug(`BasicSynth play`, event)

    const synth = this.#synth
    if (!synth) {
      return
    }
    const { type, note } = event
    const name = midiToNote(note.number)
    /**  Handle events  */
    if (type === 'noteon') {
      const { value: velocity } = event
      synth.triggerAttack(name, time, velocity as number)
    } else if (type === 'noteoff') {
      synth.triggerRelease(name, time)
    } else {
      throw new Error(`Unknown event type ${type}`)
    }
  }
}
